import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LifeBuoy, AlertTriangle, MessageCircle, HelpCircle, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const CATEGORIES = [
  { value: "operation", label: "操作の質問", icon: HelpCircle, hint: "使い方や操作方法がわからない" },
  { value: "bug", label: "不具合報告", icon: AlertTriangle, hint: "動作がおかしい、エラーが出る" },
  { value: "other", label: "その他", icon: MoreHorizontal, hint: "ご要望・上記以外のご相談" },
];

export function InquiryDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const [category, setCategory] = useState("operation");
  const [priority, setPriority] = useState("normal");
  const [body, setBody] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [sending, setSending] = useState(false);
  const [sentResult, setSentResult] = useState(null);

  const reset = () => {
    setCategory("operation");
    setPriority("normal");
    setBody("");
    setEmail(user?.email || "");
    setSentResult(null);
  };

  const submit = async () => {
    if (body.trim().length < 5) {
      toast.error("内容を5文字以上ご記入ください");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/inquiries", { category, priority, body, email: email || null });
      setSentResult(data);
      toast.success("お問い合わせを受け付けました");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(reset, 200);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="inquiry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="w-5 h-5 text-blue-600" />お問い合わせ
          </DialogTitle>
        </DialogHeader>

        {sentResult ? (
          <div className="text-center py-8 px-2" data-testid="inquiry-sent-message">
            <div className="text-4xl mb-2">✅</div>
            <h3 className="font-bold text-slate-900 mb-1">受け付けました</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              内容を確認のうえ、<b className="text-blue-700">1〜2営業日以内</b> にご返信いたします。<br />
              {sentResult.user_email_sent && email && (
                <span className="text-xs text-slate-500 mt-2 inline-block">
                  受付確認メールを <b>{email}</b> へ送信しました
                </span>
              )}
            </p>
            <Button onClick={close} className="mt-6 bg-blue-600 hover:bg-blue-700" data-testid="inquiry-close-after-send">閉じる</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* LINE 爆速サポート (最優先) */}
            <a
              href="https://lin.ee/qUC5agm"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="inquiry-line-banner"
              className="block rounded-xl border-2 border-[#06C755]/40 bg-gradient-to-br from-[#06C755]/10 to-[#06C755]/5 hover:from-[#06C755]/15 hover:to-[#06C755]/10 p-4 transition-all hover:-translate-y-0.5"
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-[#06C755] flex items-center justify-center shrink-0">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold text-[#06C755] tracking-widest">LINE 爆速サポート</div>
                  <p className="text-sm font-bold text-slate-900 leading-tight mt-0.5">
                    営業時間中はその場で回答
                  </p>
                  <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">
                    LINE で送ってもらうのが一番早いです。タップで友だち追加へ。
                  </p>
                </div>
              </div>
            </a>

            <div className="flex items-center gap-3 my-1">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[10px] text-slate-400 font-bold">LINEが使えない方</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <p className="text-xs text-slate-500 leading-relaxed">
              下のフォームから送信もできます。サポートチームが対応します。
            </p>

            {/* カテゴリ */}
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1.5">カテゴリ</label>
              <div className="grid grid-cols-3 gap-2">
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  const active = category === c.value;
                  return (
                    <button
                      key={c.value} type="button"
                      onClick={() => setCategory(c.value)}
                      data-testid={`inquiry-cat-${c.value}`}
                      className={`flex flex-col items-center gap-1 p-2.5 rounded-lg border-2 transition-all ${
                        active ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 hover:border-slate-300 text-slate-600"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-[11px] font-bold leading-tight text-center">{c.label}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-400 mt-1">{CATEGORIES.find((c) => c.value === category)?.hint}</p>
            </div>

            {/* 緊急度 */}
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1.5">緊急度</label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setPriority("urgent")} data-testid="inquiry-priority-urgent"
                  className={`p-2.5 rounded-lg border-2 transition-all text-sm font-bold ${
                    priority === "urgent" ? "border-rose-500 bg-rose-50 text-rose-700" : "border-slate-200 hover:border-slate-300 text-slate-600"
                  }`}>
                  🔥 すぐ困ってる
                </button>
                <button type="button" onClick={() => setPriority("normal")} data-testid="inquiry-priority-normal"
                  className={`p-2.5 rounded-lg border-2 transition-all text-sm font-bold ${
                    priority === "normal" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 hover:border-slate-300 text-slate-600"
                  }`}>
                  💬 いつでもOK
                </button>
              </div>
            </div>

            {/* 本文 */}
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1.5">内容 <span className="text-rose-500">*</span></label>
              <textarea
                value={body} onChange={(e) => setBody(e.target.value)}
                rows={6}
                maxLength={4000}
                placeholder="例: 配置マジックを実行したら、土曜日のスタッフが0人になってしまいます。再現手順は…"
                className="w-full text-sm border border-slate-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
                data-testid="inquiry-body"
              />
              <p className="text-[10px] text-slate-400 mt-1 text-right">{body.length}/4000</p>
            </div>

            {/* 返信先メール */}
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1.5 inline-flex items-center gap-1">
                <MessageCircle className="w-3.5 h-3.5" />返信先メール
              </label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="返信を受け取りたいメールアドレス"
                className="w-full text-sm border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-blue-300"
                data-testid="inquiry-email"
              />
              <p className="text-[10px] text-slate-400 mt-1">空欄でもOK。受付確認メールは送信されません。</p>
            </div>
          </div>
        )}

        {!sentResult && (
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={sending} data-testid="inquiry-cancel">キャンセル</Button>
            <Button onClick={submit} disabled={sending || body.trim().length < 5} className="bg-blue-600 hover:bg-blue-700" data-testid="inquiry-submit">
              {sending ? "送信中…" : "送信する"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
